import React, { useRef, useState } from 'react';
import { ServiceNodeGroup } from '@bumble/app/__generated__/data-contracts';
import { Loading } from '@bumble/app/components/Loading';
// import { PageLoading } from '@bumble/app/components/PageLoading';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface AppServicesHostingAddProps {
  appServiceId: string;
  handleClose: () => void;
}
export const AppServicesHostingAdd = ({
  appServiceId,
  handleClose,
}: AppServicesHostingAddProps) => {
  const api = useApi();
  const containerRef = useRef(null);
  // const [loadingModelOpen, setLoadingModalOpen] = useState(false);
  const [endpointServiceName, setEndpointServiceName] = useState('');
  const [azureServiceId, setAzureServiceId] = useState('');
  const [dnsName, setDnsName] = useState('');

  const [selectedAwsRegion, setSelectedAwsRegion] = React.useState('');
  const [selectedAzureRegion, setSelectedAzureRegion] = React.useState('');
  const [selectedServiceNodeGroup, setSelectedServiceNodeGroup] = React.useState('');

  const [location, setLocation] = useState('onprem');
  const handleLocationChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setLocation(value);
  };

  const addHosting = useMutation(api.appServiceAddHostingApiV1AppServiceAddHostingPost, {
    onSuccess() {
      toast.success('Hosting added.');
      handleClose();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    switch (location) {
      case 'aws':
        // setLoadingModalOpen(true);
        addHosting.mutate({
          app_service_id: appServiceId,
          location,
          region: selectedAwsRegion,
          cloud_endpoint_service_name: endpointServiceName,
        });
        break;
      case 'onprem':
        addHosting.mutate({
          app_service_id: appServiceId,
          location,
          fqdn: dnsName,
          service_node_group_id: selectedServiceNodeGroup,
        });
        break;
      case 'azure':
        addHosting.mutate({
          app_service_id: appServiceId,
          location,
          region: selectedAzureRegion,
          cloud_endpoint_service_name: azureServiceId,
        });
        break;
      default:
        break;
    }
  };

  const isDisabled = () => {
    switch (location) {
      case 'aws':
        return !(selectedAwsRegion && endpointServiceName);
      case 'onprem':
        return !(dnsName && selectedServiceNodeGroup);
      case 'azure':
        return !(selectedAzureRegion && azureServiceId);
      default:
        return true;
    }
  };

  const { isLoading: awsLoading, data: awsData } = useQuery(
    ['awsRegions'],
    api.listAwsRegionsApiV1ListAwsRegionsGet.bind(null, {}),
    { enabled: true },
  );

  const { isLoading: azureLoading, data: azureData } = useQuery(
    ['azureRegions'],
    api.listAzureRegionsApiV1ListAzureRegionsGet.bind(null, {}),
    { enabled: true },
  );

  const { isLoading: groupLoading, data: groupData } = useQuery(
    ['serviceNodeGroups'],
    api.listServiceNodeGroupApiV1ListServiceNodeGroupGet.bind(null, {}),
    { enabled: true },
  );

  if (awsLoading || azureLoading || groupLoading) {
    return <Loading />;
  }

  return (
    <Paper ref={containerRef} elevation={0}>
      <Box pt={2}>
        <Box>
          <FormControl fullWidth sx={{ mb: 4 }}>
            <FormLabel id="app-service-location">Location</FormLabel>
            <RadioGroup
              row
              aria-labelledby="app-service-location"
              name="app-service-group"
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2,minmax(0,1fr))',
                  md: 'repeat(3,minmax(0,1fr))',
                },
              }}
              onChange={handleLocationChange}
              value={location}
            >
              <FormControlLabel value="onprem" control={<Radio />} label="OnPrem" />
              <FormControlLabel value="aws" control={<Radio />} label="AWS" />
              <FormControlLabel value="azure" control={<Radio />} label="Azure" />
            </RadioGroup>
          </FormControl>
          <Collapse in={location === 'onprem'} timeout="auto">
            <TextField
              fullWidth
              id="dns-name"
              label="App's local FQDN name or IP address"
              variant="outlined"
              sx={{ mb: 4 }}
              value={dnsName}
              onChange={(e) => setDnsName(e.target.value)}
            />
            {groupData && groupData?.data.length > 0 ? (
              <FormControl fullWidth sx={{ mb: 4 }}>
                <InputLabel id="service-nodes-label">Service Node Groups</InputLabel>
                <Select
                  labelId="service-nodes-label"
                  id="service-nodes"
                  label="Service Node Groups"
                  value={selectedServiceNodeGroup}
                  onChange={(e) => setSelectedServiceNodeGroup(e.target.value)}
                >
                  {groupData?.data.map(
                    ({ service_node_group_name, service_node_group_id }: ServiceNodeGroup) => (
                      <MenuItem value={service_node_group_id} key={service_node_group_id}>
                        {service_node_group_name}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            ) : (
              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <Typography variant="body2" color="text.secondary">
                  No Service Node Groups found.
                </Typography>
              </Box>
            )}
          </Collapse>
          <Collapse in={location === 'aws'} timeout="auto">
            <FormControl fullWidth sx={{ mb: 4 }}>
              <InputLabel id="aws-regions-label">AWS Region</InputLabel>
              <Select
                labelId="aws-regions-label"
                id="aws-regions"
                label="AWS Region"
                value={selectedAwsRegion}
                onChange={(e) => setSelectedAwsRegion(e.target.value)}
              >
                {awsData?.data.map((region: string) => (
                  <MenuItem value={region} key={region}>
                    {region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              id="provider-endpoint-service-name"
              label="AWS Endpoint Service Name"
              variant="outlined"
              sx={{ mb: 4 }}
              value={endpointServiceName}
              onChange={(e) => setEndpointServiceName(e.target.value)}
            />
          </Collapse>
          <Collapse in={location === 'azure'} timeout="auto">
            <FormControl fullWidth sx={{ mb: 4 }}>
              <InputLabel id="azure-regions-label">Azure Region</InputLabel>
              <Select
                labelId="azure-regions-label"
                id="azure-regions"
                label="Azure Region"
                value={selectedAzureRegion}
                onChange={(e) => setSelectedAzureRegion(e.target.value)}
              >
                {azureData?.data.map((region: string) => (
                  <MenuItem value={region} key={region}>
                    {region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              id="private-link-service-id"
              label="Private Link Service ID"
              variant="outlined"
              sx={{ mb: 4 }}
              value={azureServiceId}
              onChange={(e) => setAzureServiceId(e.target.value)}
            />
          </Collapse>
          <Box display="flex" justifyContent="space-between" sx={{ mt: 10 }}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              onClick={handleSubmit}
              disabled={isDisabled()}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Box>
      {/* {loadingModelOpen && <PageLoading content="Working on it..." />} */}
    </Paper>
  );
};
